/**
 * Verificação do sorteio no navegador de quem consulta — sem confiar no
 * servidor. Recebe os dados de verificação (`GET /sorteios-publicos/:id/
 * dados-verificacao`) e refaz, com Web Crypto (`crypto.subtle`), cada passo do
 * algoritmo `TEG-SORTEIO-v1` (seção 2.3 do contrato da frente F08):
 *
 * 1. SHA-256 da semente revelada (hex → bytes) = hash da semente;
 * 2. SHA-256 das linhas canônicas, cada uma com `\n` = hash do universo;
 * 3. classificação de cada estrato por HMAC-SHA256(semente, protocolo),
 *    empate por protocolo, e situação pelas vagas = resultado publicado;
 * 4. SHA-256 do resultado recalculado = hash do resultado;
 * 5. trilha: cada evento aponta o hash do anterior (o primeiro, 64 zeros), o
 *    material começa pelo hash anterior e o SHA-256 do material é o hash.
 *
 * Função pura, sem React; o teste está em `verificacao.spec.ts`.
 */

import type { DadosDeVerificacao, Situacao } from './contrato';

export type ResultadoDaChecagem = 'CONFERE' | 'NAO_CONFERE' | 'NAO_SE_APLICA';

export type IdDaChecagem = 'SEMENTE' | 'UNIVERSO' | 'CLASSIFICACAO' | 'RESULTADO' | 'TRILHA';

export interface Checagem {
  id: IdDaChecagem;
  titulo: string;
  resultado: ResultadoDaChecagem;
  /** O que foi comparado, ou onde divergiu, em linguagem de gente. */
  detalhe: string;
}

export interface VerificacaoLocal {
  /** Verdadeiro só se nenhuma checagem aplicável divergiu. */
  confere: boolean;
  checagens: Checagem[];
}

export const HASH_INICIAL_DA_TRILHA = '0'.repeat(64);

const codificador = new TextEncoder();

export function hexParaBytes(hex: string): Uint8Array<ArrayBuffer> | null {
  const limpo = hex.trim();
  if (limpo.length % 2 !== 0 || !/^[0-9a-fA-F]*$/.test(limpo)) return null;
  const bytes = new Uint8Array(limpo.length / 2);
  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = parseInt(limpo.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

export function bytesParaHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Ordem de bytes (a `COLLATE "C"` do banco): negativo, zero ou positivo. */
export function compararBytes(a: Uint8Array, b: Uint8Array): number {
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i += 1) {
    if (a[i] !== b[i]) return a[i]! - b[i]!;
  }
  return a.length - b.length;
}

function compararTexto(a: string, b: string): number {
  return compararBytes(codificador.encode(a), codificador.encode(b));
}

async function sha256Hex(
  sutil: SubtleCrypto,
  dados: string | Uint8Array<ArrayBuffer>,
): Promise<string> {
  const bytes = typeof dados === 'string' ? codificador.encode(dados) : dados;
  return bytesParaHex(new Uint8Array(await sutil.digest('SHA-256', bytes)));
}

interface Participante {
  dreCodigo: string;
  modalidade: string;
  vagas: number;
  protocolo: string;
}

export interface LinhaDoResultado {
  dreCodigo: string;
  modalidade: string;
  posicao: number;
  protocolo: string;
  situacao: Situacao;
}

/** Campos 0 a 3 da linha canônica; o nome (campo 4) não entra no cálculo. */
function lerLinha(linha: string): Participante | null {
  const campos = linha.split('|');
  if (campos.length < 5) return null;
  const vagas = Number(campos[2]);
  if (!Number.isInteger(vagas) || vagas < 0 || !campos[0] || !campos[1] || !campos[3]) return null;
  return { dreCodigo: campos[0], modalidade: campos[1], vagas, protocolo: campos[3] };
}

/**
 * Refaz a classificação a partir das linhas e da semente. Estratos em ordem de
 * bytes de (código da DRE, modalidade); dentro de cada um, HMAC crescente.
 */
export async function classificar(
  participantes: Participante[],
  semente: Uint8Array<ArrayBuffer>,
  sutil: SubtleCrypto,
): Promise<LinhaDoResultado[]> {
  const chave = await sutil.importKey('raw', semente, { name: 'HMAC', hash: 'SHA-256' }, false, [
    'sign',
  ]);
  const estratos = new Map<
    string,
    {
      dreCodigo: string;
      modalidade: string;
      vagas: number;
      itens: { protocolo: string; hmac: Uint8Array }[];
    }
  >();
  for (const p of participantes) {
    const chaveDoEstrato = `${p.dreCodigo}\u0000${p.modalidade}`;
    const estrato = estratos.get(chaveDoEstrato) ?? {
      dreCodigo: p.dreCodigo,
      modalidade: p.modalidade,
      vagas: p.vagas,
      itens: [],
    };
    const hmac = new Uint8Array(await sutil.sign('HMAC', chave, codificador.encode(p.protocolo)));
    estrato.itens.push({ protocolo: p.protocolo, hmac });
    estratos.set(chaveDoEstrato, estrato);
  }

  const ordenados = [...estratos.values()].sort(
    (a, b) => compararTexto(a.dreCodigo, b.dreCodigo) || compararTexto(a.modalidade, b.modalidade),
  );

  const resultado: LinhaDoResultado[] = [];
  for (const estrato of ordenados) {
    estrato.itens.sort((a, b) => {
      const porHmac = compararBytes(a.hmac, b.hmac);
      if (porHmac !== 0) return porHmac;
      return a.protocolo < b.protocolo ? -1 : a.protocolo > b.protocolo ? 1 : 0;
    });
    estrato.itens.forEach((item, indice) => {
      const posicao = indice + 1;
      resultado.push({
        dreCodigo: estrato.dreCodigo,
        modalidade: estrato.modalidade,
        posicao,
        protocolo: item.protocolo,
        situacao: posicao <= estrato.vagas ? 'CONTEMPLADO' : 'SUPLENTE',
      });
    });
  }
  return resultado;
}

export function textoDoResultado(linhas: LinhaDoResultado[]): string {
  return linhas
    .map((l) => `${l.dreCodigo}|${l.modalidade}|${l.posicao}|${l.protocolo}|${l.situacao}\n`)
    .join('');
}

function checagem(
  id: IdDaChecagem,
  titulo: string,
  resultado: ResultadoDaChecagem,
  detalhe: string,
): Checagem {
  return { id, titulo, resultado, detalhe };
}

const TITULOS: Record<IdDaChecagem, string> = {
  SEMENTE: 'A semente revelada é a do compromisso',
  UNIVERSO: 'A lista de participantes é a congelada no preparo',
  CLASSIFICACAO: 'A classificação recalculada é a publicada',
  RESULTADO: 'O hash do resultado confere',
  TRILHA: 'A trilha de eventos está íntegra',
};

async function verificarTrilha(d: DadosDeVerificacao, sutil: SubtleCrypto): Promise<Checagem> {
  if (d.trilha.length === 0) {
    return checagem('TRILHA', TITULOS.TRILHA, 'NAO_CONFERE', 'A trilha veio vazia.');
  }
  const eventos = [...d.trilha].sort((a, b) => a.seq - b.seq);
  let anterior = HASH_INICIAL_DA_TRILHA;
  for (const evento of eventos) {
    const hashAnterior = evento.hashAnterior.toLowerCase();
    if (hashAnterior !== anterior) {
      return checagem(
        'TRILHA',
        TITULOS.TRILHA,
        'NAO_CONFERE',
        `O evento ${evento.seq} não aponta para o hash do evento anterior.`,
      );
    }
    if (!evento.material.startsWith(`${evento.hashAnterior}|${evento.seq}|${d.sorteioId}|`)) {
      return checagem(
        'TRILHA',
        TITULOS.TRILHA,
        'NAO_CONFERE',
        `O material do evento ${evento.seq} não começa pelo hash anterior, pela sequência e pelo sorteio.`,
      );
    }
    const hash = await sha256Hex(sutil, evento.material);
    if (hash !== evento.hash.toLowerCase()) {
      return checagem(
        'TRILHA',
        TITULOS.TRILHA,
        'NAO_CONFERE',
        `O hash do evento ${evento.seq} não é o SHA-256 do material dele.`,
      );
    }
    anterior = hash;
  }
  return checagem(
    'TRILHA',
    TITULOS.TRILHA,
    'CONFERE',
    `${eventos.length} evento(s) encadeado(s), do primeiro ao último.`,
  );
}

/** Refaz a verificação inteira. Nunca lança: problema de dado vira checagem que não confere. */
export async function verificarNoNavegador(
  d: DadosDeVerificacao,
  sutil: SubtleCrypto = globalThis.crypto.subtle,
): Promise<VerificacaoLocal> {
  const checagens: Checagem[] = [];

  // 1. Semente × compromisso.
  const semente = d.sementeRevelada ? hexParaBytes(d.sementeRevelada) : null;
  if (!d.sementeRevelada) {
    checagens.push(
      checagem(
        'SEMENTE',
        TITULOS.SEMENTE,
        'NAO_SE_APLICA',
        'A semente só é revelada na execução. Até lá vale o compromisso publicado.',
      ),
    );
  } else if (!semente || semente.length !== 32) {
    checagens.push(
      checagem(
        'SEMENTE',
        TITULOS.SEMENTE,
        'NAO_CONFERE',
        'A semente revelada não tem 32 bytes em hexadecimal.',
      ),
    );
  } else {
    const hash = await sha256Hex(sutil, semente);
    checagens.push(
      hash === d.sementeHash.toLowerCase()
        ? checagem(
            'SEMENTE',
            TITULOS.SEMENTE,
            'CONFERE',
            'SHA-256 da semente = hash registrado no preparo.',
          )
        : checagem(
            'SEMENTE',
            TITULOS.SEMENTE,
            'NAO_CONFERE',
            'O SHA-256 da semente revelada é diferente do compromisso registrado no preparo.',
          ),
    );
  }

  // 2. Universo.
  const participantes: Participante[] = [];
  let linhaInvalida: number | null = null;
  for (let indice = 0; indice < d.linhas.length; indice += 1) {
    const p = lerLinha(d.linhas[indice]!);
    if (p) participantes.push(p);
    else if (linhaInvalida === null) linhaInvalida = indice + 1;
  }
  const universoHash = await sha256Hex(sutil, d.linhas.map((l) => `${l}\n`).join(''));
  if (linhaInvalida !== null) {
    checagens.push(
      checagem(
        'UNIVERSO',
        TITULOS.UNIVERSO,
        'NAO_CONFERE',
        `A linha ${linhaInvalida} está fora do formato canônico.`,
      ),
    );
  } else if (universoHash !== d.universoHash.toLowerCase()) {
    checagens.push(
      checagem(
        'UNIVERSO',
        TITULOS.UNIVERSO,
        'NAO_CONFERE',
        'O SHA-256 das linhas é diferente do hash do universo registrado no preparo.',
      ),
    );
  } else {
    checagens.push(
      checagem(
        'UNIVERSO',
        TITULOS.UNIVERSO,
        'CONFERE',
        `${d.linhas.length} participante(s); SHA-256 das linhas = hash do universo.`,
      ),
    );
  }

  // 3 e 4. Classificação e hash do resultado — só com a semente revelada.
  if (!semente || semente.length !== 32 || linhaInvalida !== null) {
    const motivo = !d.sementeRevelada
      ? 'A classificação sai na execução, quando a semente é revelada.'
      : 'Sem semente e linhas válidas não há como recalcular.';
    const aplicavel = d.sementeRevelada ? 'NAO_CONFERE' : 'NAO_SE_APLICA';
    checagens.push(checagem('CLASSIFICACAO', TITULOS.CLASSIFICACAO, aplicavel, motivo));
    checagens.push(checagem('RESULTADO', TITULOS.RESULTADO, aplicavel, motivo));
  } else {
    const recalculado = await classificar(participantes, semente, sutil);
    const publicado = new Map(
      d.resultado.map((r) => [`${r.dreCodigo}|${r.modalidade}|${r.protocolo}`, r]),
    );
    const divergentes = recalculado.filter((r) => {
      const p = publicado.get(`${r.dreCodigo}|${r.modalidade}|${r.protocolo}`);
      return !p || p.posicao !== r.posicao || p.situacao !== r.situacao;
    });
    const tamanhoDiferente = publicado.size !== recalculado.length;
    if (divergentes.length > 0 || tamanhoDiferente) {
      const exemplos = divergentes
        .slice(0, 3)
        .map(
          (r) =>
            `${r.protocolo} (deveria ser ${r.posicao}º, ${r.situacao === 'CONTEMPLADO' ? 'contemplado' : 'suplente'})`,
        )
        .join('; ');
      checagens.push(
        checagem(
          'CLASSIFICACAO',
          TITULOS.CLASSIFICACAO,
          'NAO_CONFERE',
          divergentes.length > 0
            ? `${divergentes.length} posição(ões) diferente(s) do publicado: ${exemplos}.`
            : `O resultado publicado tem ${publicado.size} linha(s); o recalculado, ${recalculado.length}.`,
        ),
      );
    } else {
      checagens.push(
        checagem(
          'CLASSIFICACAO',
          TITULOS.CLASSIFICACAO,
          'CONFERE',
          `${recalculado.length} posição(ões) recalculada(s), todas iguais às publicadas.`,
        ),
      );
    }
    const resultadoHash = await sha256Hex(sutil, textoDoResultado(recalculado));
    checagens.push(
      d.resultadoHash && resultadoHash === d.resultadoHash.toLowerCase()
        ? checagem(
            'RESULTADO',
            TITULOS.RESULTADO,
            'CONFERE',
            'SHA-256 do resultado recalculado = hash publicado.',
          )
        : checagem(
            'RESULTADO',
            TITULOS.RESULTADO,
            'NAO_CONFERE',
            'O SHA-256 do resultado recalculado é diferente do hash publicado.',
          ),
    );
  }

  // 5. Trilha.
  checagens.push(await verificarTrilha(d, sutil));

  return {
    confere: checagens.every((c) => c.resultado !== 'NAO_CONFERE'),
    checagens,
  };
}
